import { useEffect, useState } from "react";

import {
  Button,
  Icon,
  InfoCircleHollowSVG,
  TooltipDialog,
} from "@newsela/angelou";

import Slider from "@mui/material/Slider";

import { withStyles } from "tss-react/mui";
import { useSearchPageContext } from "@/search/Langston/SearchPageProvider";
import { useSearchAdminPermissions } from "@/search/Langston/useSearchAdminPermissions";
import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";
import { MATURITY_RANGE_VALUES } from "@/search/Langston/SearchPage/SearchPage.constants";
import {
  filterAndTooltipContainerCss,
  subHeadingCss,
  tooltipDialogCss,
  tooltipIconCss,
} from "@/search/Langston/FilterPanel/filters/ReadingLevels/ReadingLevels.styles";
import {
  getAriaLabel,
  getMaturityRangeFromRange,
  getRangeFromParams,
  sliderMarks,
} from "./MaturityRangeSlider.utils";
import {
  sliderCss,
  sliderValueLabelWrapperCss,
} from "./MaturityRangeSlider.styles";

const StyledSlider = withStyles(Slider, sliderCss);

const minimumMaturityKey = LangstonParameterSchema.minimum_maturity.key;
const maximumMaturityKey = LangstonParameterSchema.maximum_maturity.key;

export const MaturityRangeSlider = () => {
  const {
    getters: { isDisabledFilters, searchParams },
    handlers: { getSingleSearchParamValue, setMaturityLevelParam },
  } = useSearchPageContext();

  const { blockActionAndShowConfirmationModal, blockFilterChanges } =
    useSearchAdminPermissions();

  const minParamValue = parseInt(getSingleSearchParamValue(minimumMaturityKey));
  const maxParamValue = parseInt(getSingleSearchParamValue(maximumMaturityKey));

  const [range, setRange] = useState(
    getRangeFromParams(minParamValue, maxParamValue) || [0, 3],
  );

  useEffect(() => {
    if (!minParamValue && !maxParamValue) {
      setRange([0, 3]);
    } else {
      setRange(getRangeFromParams(minParamValue, maxParamValue));
    }
  }, [searchParams]);

  const handleChange = (_: Event, maturityRange: number | number[]) => {
    if (typeof maturityRange === "number") return;

    if (blockFilterChanges) {
      blockActionAndShowConfirmationModal();
      return;
    }

    setRange(maturityRange);

    const { min, max } = getMaturityRangeFromRange(maturityRange);

    !!min && !!max && setMaturityLevelParam(min, max);
  };

  return (
    <>
      <div css={filterAndTooltipContainerCss}>
        <h3 css={subHeadingCss}>Maturity level</h3>
        <TooltipDialog
          offset={8}
          placement="bottom-start"
          header="Maturity level"
          body={
            <>
              This filters the maturity of the content.
              <br />
              <br />
              For example, an article about the summer olympics would have an
              Upper Elementary School – Middle School rating.
            </>
          }
          buttonProps={{
            flavor: Button.flavors.incognito,
          }}
          ariaProps={{
            "aria-label": "Maturity Level Filter Tooltip",
          }}
          __cssFor={tooltipDialogCss}
          buttonContents={
            <Icon
              SvgComponent={InfoCircleHollowSVG}
              __cssFor={tooltipIconCss}
            />
          }
        />
      </div>

      <StyledSlider
        onChange={handleChange}
        getAriaLabel={getAriaLabel}
        getAriaValueText={(val) => MATURITY_RANGE_VALUES[val]?.ariaLabel || ""}
        marks={sliderMarks}
        min={0}
        max={3}
        step={1}
        valueLabelDisplay="on"
        valueLabelFormat={(placementIndex) => {
          const maturityRange = MATURITY_RANGE_VALUES[placementIndex];
          return (
            <div css={sliderValueLabelWrapperCss}>
              {maturityRange ? maturityRange.label : ""}
            </div>
          );
        }}
        value={range}
        disabled={isDisabledFilters}
        data-qa-props="search-maturity-range-slider"
      />
    </>
  );
};
